import { action, computed, observable, values, makeObservable } from 'mobx';

import { Features } from 'APP/packages/features/Features';
import SearchService from 'SERVICES/search';

import { Bot } from './User/Bot/Bot';
import { Me } from './User/Me/Me';
import { User } from './User/User';

// TODO: fix any types

interface IUserContact {
  id: string;
  updateTs: number;
}
export class UsersStore {
  @observable data = observable.map<string, User>({});
  @observable onlineStatuses = observable.map({});
  @observable _userContacts: IUserContact[] = [];
  @observable isLoadedContacts = false;
  @observable myId: string | null = null;
  @observable isUserContactsLoading = false;

  constructor() {
    makeObservable(this);
  }

  @computed
  get userContacts(): IUserContact[] {
    if (!Features.isContactsEnabled()) {
      return this.users
        .map((user) => {
          if (!user.isMe && !user.isBot) {
            return { id: user.id, updateTs: Date.now() };
          }
          return null;
        })
        .filter((x) => !!x);
    }
    return this._userContacts;
  }

  @action
  merge(data: any): void {
    this.data.merge(data);
  }

  @action
  mergeOnlineStatuses(data: any): void {
    this.onlineStatuses.merge(data);
  }

  @action
  resetOnlineStatuses(): void {
    this.onlineStatuses.clear();
  }

  @action
  reset(): void {
    this.data.clear();
    this.onlineStatuses.clear();
    this._userContacts = [];
    this.isLoadedContacts = false;
    this.myId = null;
  }

  getUserById(id: string): User | null {
    return this.data.get(id) || null;
  }

  getUserByNickName(nickName: string): User | null {
    return this.users.find((storeUser) => storeUser.nickName.trim() === nickName.trim()) || null;
  }

  getUserByEmail(email: string): User | null {
    return this.users.find((storeUser) => storeUser.email === email) || null;
  }

  getOnlineStatusByUserId(userId: string): any {
    return this.onlineStatuses.get(userId);
  }

  @action
  setIsUserContactsLoading(isUserContactsLoading: boolean): void {
    this.isUserContactsLoading = isUserContactsLoading;
  }

  @action
  setContacts(contacts: any): void {
    this._userContacts = contacts.map((contact: any) => {
      const existingContact = this._userContacts.find(({ id }) => id === contact.id);
      return existingContact || { id: contact.id, updateTs: Date.now() };
    });
  }

  @action
  addContact(newContactId: string): void {
    const isContactExistsInStore = this._userContacts.some(({ id }) => id === newContactId);

    if (!isContactExistsInStore) {
      this._userContacts.push({ id: newContactId, updateTs: Date.now() });
    } else {
      const contactIndex = this._userContacts.findIndex(({ id }) => id === newContactId);
      this._userContacts[contactIndex].updateTs = Date.now();
    }
  }

  @action
  setLoadedContacts(isLoadedContacts: boolean): void {
    this.isLoadedContacts = isLoadedContacts;
  }

  @action
  addMe(data: any): void {
    const prevMe = this.getUserById(data.id);
    const me = new Me(this, data);

    if (prevMe instanceof Me && prevMe?.accountProviders.length) {
      me.setAccountProviders(prevMe.accountProviders);
    }

    this.myId = me.id;
    this.data.set(me.id, me);
  }

  @action
  add(newUsers: any): void {
    const newUsersMap = newUsers.map((user: any) => {
      if (user.id && parseInt(user.id, 10) < 0) {
        return [user.id, new Bot(this, user)];
      }

      if (user.id === this.myId) {
        return [user.id, new Me(this, user)];
      }

      return [user.id, new User(this, user)];
    });

    this.merge(newUsersMap);
  }

  @action
  delete(userId: string): void {
    this.data.delete(userId);
  }

  @computed
  get Me(): Me {
    return this.getUserById(this.myId!) as Me;
  }

  @computed
  get users(): ReadonlyArray<User> {
    return values(this.data);
  }

  @computed
  get contacts(): User[] {
    return this.users
      .filter((contact) => contact.isInPhoneBook && !contact.isMe && !contact.isBlocked)
      .sort((cn1, cn2) => (cn1.nameToSort > cn2.nameToSort ? 1 : -1));
  }

  @computed
  get hasContacts(): boolean {
    return this.contacts.length > 0;
  }

  getFilteredContacts(
    excludedChats: string[],
    excludedUsers: string[],
    searchQuery: string,
    withMe = false
  ): User[] {
    const contacts = this.contacts.filter(
      (contact) =>
        !excludedChats.includes(contact.id) &&
        !excludedUsers.includes(contact.id) &&
        !contact.isBlockedByMe &&
        !contact.isBlockedMe
    );

    if (withMe) {
      contacts.push(this.Me);
    }

    if (!searchQuery || !searchQuery.length) {
      return contacts;
    }

    return contacts
      .filter((contact) => {
        return SearchService.searchInContactData(contact, searchQuery);
      })
      .sort((a, b) => {
        return a.nameToSort > b.nameToSort ? 1 : -1;
      });
  }

  searchContactsByString(searchString: string): User[] {
    const contacts = this.contacts;

    if (searchString.length) {
      return contacts.filter((contact) => {
        const { contactName, userName, nickName, phone, displayPhone } = contact;

        return (
          this.getSearchCondition(contactName, searchString) ||
          this.getSearchCondition(userName, searchString) ||
          this.getSearchCondition(nickName, searchString) ||
          this.getSearchCondition(phone, searchString.replace('+', '')) ||
          this.getSearchCondition(displayPhone, searchString.replace('+', ''))
        );
      });
    }

    return contacts;
  }

  private getSearchCondition(field: string | null, searchString: string): boolean {
    return field
      ? field.trim().toLowerCase().indexOf(searchString.toLowerCase().trim()) !== -1
      : false;
  }
}
